// react
import type { ReactNode } from 'react';
// libraries
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

// material ui
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
} from '@mui/material';
import type { SelectProps } from '@mui/material';

// components
import { inputSurfaceStyles } from '@/theme/Inputs/textFieldCustomization';

// relative
import RHFFieldLabel from './RHFFieldLabel';

export type RHFSelectOption<TValue extends string | number = string> = {
  value: TValue;
  label: ReactNode;
  /** Optional leading icon (or any node) rendered before the label, both in
   * the dropdown list and in the closed select's displayed value - e.g. a
   * `SeverityBars` indicator per option. */
  icon?: ReactNode;
  disabled?: boolean;
  testId?: string;
};

export type RHFSelectProps<
  TFieldValues extends FieldValues,
  TValue extends string | number = string,
> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  options: RHFSelectOption<TValue>[];
  label?: ReactNode;
  rules?: UseControllerProps<TFieldValues>['rules'];
  fullWidth?: boolean;
  helperText?: ReactNode;
} & Omit<
  SelectProps,
  | 'name'
  | 'defaultValue'
  | 'label'
  | 'error'
  | 'value'
  | 'onChange'
  | 'helperText'
>;

export default function RHFSelect<
  TFieldValues extends FieldValues,
  TValue extends string | number = string,
>({
  name,
  control,
  options,
  label,
  rules,
  fullWidth = true,
  required,
  helperText,
  sx,
  ...selectProps
}: RHFSelectProps<TFieldValues, TValue>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <FormControl
          fullWidth={fullWidth}
          error={!!fieldState.error}
          required={required}
        >
          {label ? (
            <RHFFieldLabel htmlFor={name} required={required}>
              {label}
            </RHFFieldLabel>
          ) : null}

          <Select
            {...field}
            {...selectProps}
            id={name}
            required={required}
            variant="outlined"
            fullWidth={fullWidth}
            value={field.value ?? ''}
            sx={[
              inputSurfaceStyles,
              ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
          >
            {options.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                data-testid={option.testId}
              >
                {option.icon ? (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {option.icon}
                    {option.label}
                  </Box>
                ) : (
                  option.label
                )}
              </MenuItem>
            ))}
          </Select>

          {fieldState.error?.message ? (
            <FormHelperText sx={{ ml: 4 }}>
              {fieldState.error.message}
            </FormHelperText>
          ) : helperText ? (
            <FormHelperText>{helperText}</FormHelperText>
          ) : null}
        </FormControl>
      )}
    />
  );
}
